import React, {useState, useEffect, useMemo} from "react";
import dayjs from "dayjs";
import {Select} from "antd";
import Spinner from "../../../components/icons/Spinner";
import {useCubeQuery} from "../../../utils/hooks/useCubeQuery";
import useCubeClient from "../../../utils/cubejs-client";
import DateSelect from "../../../components/buttons/DateSelect";
import Table from "../../../components/lists/Table/Table";
import Big from 'big.js';
import {gql, useQuery} from "@apollo/client";

const transformCampaignType = (type) => {
  if (type === 'STANDARD') return 'Marketplace';
  return type.charAt(0) + type.slice(1).toLowerCase().replace('_', ' ');
};

function transformData(data) {
  const groupedData = data.reduce((acc, item) => {
    const campaignId = item["aggregateTdLevel.campaignId"];
    if (!acc[campaignId]) acc[campaignId] = [];
    acc[campaignId].push(item);
    return acc;
  }, {});

  const transformedData = Object.keys(groupedData).map(campaignId => {
    const toBig = (value, defaultValue = 0) => {
      if (value === undefined || value === null || value === '' || isNaN(value)) {
        return new Big(defaultValue);
      }
      return new Big(value);
    };

    const items = groupedData[campaignId];
    const totalReads = items.reduce((acc, item) => acc + (item["aggregateTdLevel.reads"] || 0), 0);
    const totalPublisherRevenue = items.reduce((acc, item) => acc.plus(toBig(item["aggregateTdLevel.publisherRevenue"])), toBig(0));
    const totalClicks = items.reduce((acc, item) => acc + (item["aggregateTdLevel.tdClicks"] || 0), 0);
    const totalImpressions = items.reduce((acc, item) => acc.plus(toBig(item["aggregateTdLevel.tdImpressions"])), toBig(0));

    const eCpm = totalImpressions.gt(0)
      ? totalPublisherRevenue.div(totalImpressions.div(1000)).round(2).toNumber()
      : 0;

    const children = items.map(item => ({
      advertiserName: item["aggregateTdLevel.advertiserName"],
      campaignType: item["aggregateTdLevel.campaignType"],
      campaignName: item["aggregateTdLevel.websiteName"],
      billingId: undefined,
      eCpm: toBig(item["aggregateTdLevel.eCpm"]).round(2).toNumber(),
      reads: item["aggregateTdLevel.reads"] || 0,
      publisherRevenue: toBig(item["aggregateTdLevel.publisherRevenue"]).toFixed(2),
      tdClicks: item["aggregateTdLevel.tdClicks"] || 0,
      impressions: item["aggregateTdLevel.tdImpressions"] || 0
    }));

    return {
      campaignName: items[0]["aggregateTdLevel.campaignName"],
      campaignId: campaignId,
      advertiserName: items[0]["aggregateTdLevel.advertiserName"],
      campaignType: items[0]["aggregateTdLevel.campaignType"],
      billingId: undefined,
      children: children,
      eCpm: eCpm,
      reads: totalReads,
      publisherRevenue: totalPublisherRevenue.toFixed(2),
      tdClicks: totalClicks,
      tdImpressions: totalImpressions.toFixed(0)
    };
  });

  return transformedData;
}

const GET_CAMPAIGN_BILLING_IDS = gql`
  query GetCampaignBillingIds($filters: CampaignFilterArgs, $perPage: Int) {
    campaigns: CampaignMany(filters: $filters, perPage: $perPage) {
      items {
        _id
        billingId
      }
    }
  }
`;

const AccountReportingII = () => {
  const cubejsApi = useCubeClient()
  const [dateRange, setDateRange] = useState([dayjs().add(-730, "d"), dayjs()]);
  const [types, setTypes] = useState(['STANDARD', 'FILLER', 'IN_HOUSE', 'MARKETPLACE_NATIVE_DISPLAY', 'IN_HOUSE_NATIVE_DISPLAY']);
  const [websites, setWebsites] = useState([]);
  const [uniquewebsites, setUnique] = useState([]);

  const handleDate = (newvalue) => setDateRange(newvalue);

  const columns = [
    {
      title: "Campaign Name",
      dataIndex: "campaignName",
      key: "campaignName",
      sorter: (a, b) => {
        if (a.campaignName && b.campaignName) return a.campaignName.localeCompare(b.campaignName);
        return a.campaignName ? -1 : 1;
      }
    },
    {
      title: "Billing ID",
      dataIndex: "billingId",
      key: "billingId",
      sorter: (a, b) => {
        const aId = a.billingId || "";
        const bId = b.billingId || "";
        return aId.localeCompare(bId);
      },
      render: (value) => value || "-",
    },
    {
      title: "Advertiser",
      dataIndex: "advertiserName",
      key: "advertiserName",
    },
    {
      title: "Campaign Type",
      dataIndex: "campaignType",
      key: "campaignType",
      render: (text) => transformCampaignType(text),
    },
    {
      title: "eCPM",
      dataIndex: "eCpm",
      key: "eCPM",
      sorter: (a, b) => a.eCpm - b.eCpm,
    },
    {
      title: "Revenue",
      dataIndex: "publisherRevenue",
      key: "publisherRevenue",
      sorter: (a, b) => a.publisherRevenue - b.publisherRevenue,
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Reads",
      dataIndex: "reads",
      key: "reads",
      sorter: (a, b) => a.reads - b.reads,
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Clicks",
      dataIndex: "tdClicks",
      key: "tdClicks",
      sorter: (a, b) => a.tdClicks - b.tdClicks,
      render: (value) => parseInt(value || 0).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Impressions",
      dataIndex: "tdImpressions",
      key: "tdImpressions",
      sorter: (a, b) => a.tdImpressions - b.tdImpressions,
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
  ];

  const {
    resultSet: campiData,
    error: campiError,
    isLoading: campiLoading,
  } = useCubeQuery({
    dimensions: [
      "aggregateTdLevel.campaignName",
      "aggregateTdLevel.campaignId",
      "aggregateTdLevel.advertiserName",
      "aggregateTdLevel.websiteName",
      "aggregateTdLevel.websiteId",
      "aggregateTdLevel.campaignType",
    ],
    measures: [
      "aggregateTdLevel.eCpm",
      "aggregateTdLevel.reads",
      "aggregateTdLevel.publisherRevenue",
      "aggregateTdLevel.tdImpressions",
      "aggregateTdLevel.tdClicks",
    ],
    timeDimensions: [
      {
        dimension: "aggregateTdLevel.date",
        dateRange: dateRange,
      }],
    filters: [
      {
        "member": "aggregateTdLevel.websiteId",
        "operator": "set"
      },
      {
        "member": "aggregateTdLevel.publisherId",
        "operator": "set"
      },
      ...(types.length > 0
        ? [
          {
            member: "aggregateTdLevel.campaignType",
            operator: "equals",
            values: types,
          },
        ]
        : []),

      ...(websites.length > 0
        ? [
          {
            member: "aggregateTdLevel.websiteId",
            operator: "equals",
            values: websites,
          },
        ]
        : []),
    ]
  }, {cubejsApi});

  const {
    resultSet: websiteData,
    error: webError,
    isLoading: webLoading,
  } = useCubeQuery({
    dimensions: [
      "aggregateTdLevel.websiteName",
      "aggregateTdLevel.websiteId",
    ],
    measures: [
      "aggregateTdLevel.reads",
    ],
    timeDimensions: [
      {
        dimension: "aggregateTdLevel.date",
        dateRange: dateRange,
      }],
    filters: [
      {
        "member": "aggregateTdLevel.websiteId",
        "operator": "set"
      },
      {
        "member": "aggregateTdLevel.publisherId",
        "operator": "set"
      },
    ]
  }, {cubejsApi});


  const typeOptions = [
    {label: "In House", value: "IN_HOUSE"},
    {label: "In House Display", value: "IN_HOUSE_NATIVE_DISPLAY"},
    {label: "Filler", value: "FILLER"},
    {label: "Marketplace", value: "STANDARD"},
    { label: "Marketplace Display", value: "MARKETPLACE_NATIVE_DISPLAY" },

  ];

  useEffect(() => {
    if (!webLoading && websiteData && !webError) {
      const uniqueWebsiteIds = new Set();
      const uniqueWebsites = websiteData.loadResponses[0].data
        .map((item) => ({
          label: item["aggregateTdLevel.websiteName"],
          value: item["aggregateTdLevel.websiteId"],
          key: item["aggregateTdLevel.websiteId"],
        }))
        .filter((website) => {
          if (!uniqueWebsiteIds.has(website.value)) {
            uniqueWebsiteIds.add(website.value);
            return true;
          }
          return false;
        });

      // Set `websites` to be an array of unique website IDs
      setWebsites(uniqueWebsites.map((website) => website.value));
      
      setUnique(uniqueWebsites);
    }
  }, [webLoading, websiteData, webError]); // Run effect when loading status or data changes

  const cubeData = useMemo(
    () => (campiData?.loadResponses?.[0]?.data ? campiData.loadResponses[0].data : []),
    [campiData]
  );
  const tableData = useMemo(() => transformData(cubeData), [cubeData]);
  const campaignIds = useMemo(
    () => Array.from(new Set(tableData.map((row) => row.campaignId).filter(Boolean))),
    [tableData]
  );

  const {
    data: campaignDetailsData,
    loading: campaignDetailsLoading,
    error: campaignDetailsError,
  } = useQuery(GET_CAMPAIGN_BILLING_IDS, {
    skip: campaignIds.length === 0,
    variables: {
      filters: {
        _id: campaignIds,
      },
      perPage: Math.max(campaignIds.length, 1),
    },
  });

  const billingMap = useMemo(() => {
    if (!campaignDetailsData?.campaigns?.items) {
      return new Map();
    }
    return campaignDetailsData.campaigns.items.reduce((acc, item) => {
      if (!item?._id) {
        return acc;
      }
      acc.set(item._id, item.billingId || "");
      return acc;
    }, new Map());
  }, [campaignDetailsData]);

  const tableDataWithBilling = useMemo(
    () =>
      tableData.map((row) => {
        const billingId = billingMap.get(row.campaignId) || "";
        return {
          ...row,
          billingId,
          children: row.children?.map((child) => ({
            ...child,
            billingId,
          })),
        };
      }),
    [tableData, billingMap]
  );

  if (campiError || campaignDetailsError) return <div>Error loading data</div>;
  if (campiLoading || !campiData || campaignDetailsLoading) return <Spinner/>;

  return (
    <div className="pb-4 bg-white rounded-lg shadow-lg py-2">
      <div className="px-2 sm:px-6 lg:px-4 py-4">
        <div className="p-2">
          <h1 className="text-lg leading-6 font-medium text-gray-900 pb-2">
            Revenue
          </h1>
          <div className="sm:flex sm:items-center justify-between">
            <div className="sm:w-1/2">
              <DateSelect dateRange={dateRange} handleDate={handleDate}/>
            </div>
            <div className="sm:w-1/2 flex justify-center">
              <Select
                className="w-full sm:w-auto"
                mode="multiple"
                options={typeOptions}
                value={types}
                onChange={setTypes}
              />
            </div>
            <div className="sm:w-1/2 flex justify-end">
              <Select
                className="w-full sm:w-auto"
                mode="multiple"
                options={uniquewebsites}
                value={websites}
                onChange={setWebsites}
              />
            </div>
          </div>
        </div>

        <Table columns={columns} data={tableDataWithBilling} searchable={false}/>
      </div>
    </div>
  );
};

export default AccountReportingII;
